import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type Column,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type Row,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { entryStatusConstants } from 'tods-competition-factory';

import SearchInput from 'components/inputs/searchInput/SearchInput';
import { CompetitorsColumns } from 'components/tables/competitors/types';
import { handleReorderParticipants } from 'components/tables/competitors/utils';
import {
  type DragObjectItemInterface,
  type DropTypeEnum,
  type RowConfigInterface,
} from 'components/tables/dndStandardTable/types';
import EnhancedTable from 'components/tables/enhancedTable/EnhancedTable';
import { emptyObject } from 'components/utils';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

const { ALTERNATE, QUALIFIER } = entryStatusConstants;

const dragAndDropTableTypes = [ALTERNATE, QUALIFIER];
const defaultSorting = [
  {
    id: 'player_status',
    desc: false,
  },
  {
    id: 'index',
    desc: false,
  },
];

const getRowId = (row: ParticipantInterface) => row.participantId;
interface DrawParticipantsTableProps {
  additionalButtons?: React.ReactNode;
  columns: Column<ParticipantInterface>[];
  data: ParticipantInterface[];
  disableSearch?: boolean;
  id?: string;
  isDouble?: boolean;
  isEditReorder?: boolean;
  isEnableMultiRowSelection?: boolean;
  rowConfig?: RowConfigInterface;
  saveColumnsSelection?: (columns: Column<ParticipantInterface>[]) => void;
  savedColumns?: Array<string>;
  type?: 'SELECTED' | 'ALTERNATE' | 'UNPAIRED' | 'UNGROUPED' | 'WITHDRAWN' | 'QUALIFIER';
  useTable?: typeof useReactTable;
  onColumnVisibilityChange?: (columns: Column<ParticipantInterface>[]) => void;
  selectedActions?: (selectedRows: Row<ParticipantInterface>[], resetRowSelection?: () => void) => React.ReactNode;
  setEntryPositionsState?: (entryPositions: { participantId: string; entryPosition: number }[]) => void;
}

const DrawParticipantsTable = ({
  additionalButtons,
  columns,
  data,
  disableSearch = false,
  isDouble,
  isEditReorder,
  isEnableMultiRowSelection,
  rowConfig,
  saveColumnsSelection,
  savedColumns,
  selectedActions,
  setEntryPositionsState,
  type,
  useTable: useTableProp = useReactTable,
}: DrawParticipantsTableProps) => {
  const defaultHiddenColumnIds = useMemo(
    () => [isDouble ? CompetitorsColumns.SINGLES_WTN : CompetitorsColumns.DOUBLES_WTN],
    [isDouble],
  );
  const columnsIds = useMemo(() => columns?.map((column) => column.id), [columns]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    columnsIds.reduce((columnsObject, key) => {
      if (defaultHiddenColumnIds?.includes(key as CompetitorsColumns) && savedColumns?.[key] === undefined)
        return Object.assign(columnsObject, { [`${key}`]: false });
      return Object.assign(columnsObject, { [`${key}`]: savedColumns?.includes(key) });
    }, {}),
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>(
    defaultSorting.filter((sortType) => columnVisibility?.[sortType.id]),
  );

  const columnsRef = useRef<Array<string>>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const resetRowSelection = useCallback(() => setRowSelection(emptyObject), []);

  const isRowDraggable = useCallback(() => {
    return isEditReorder;
  }, [isEditReorder]);
  const isRowDroppable = useCallback(() => {
    return isEditReorder;
  }, [isEditReorder]);

  const drawRowConfig: RowConfigInterface = useMemo(
    () => ({
      ...rowConfig,
      draggableRow: dragAndDropTableTypes?.includes(type) ? isRowDraggable : undefined,
      droppableRow: dragAndDropTableTypes?.includes(type) ? isRowDroppable : undefined,
    }),
    [isRowDraggable, isRowDroppable, rowConfig, type],
  );

  const handleOnDrop = useCallback(
    (dragObject: DragObjectItemInterface<any>, endIndex: number, dropType: DropTypeEnum) =>
      handleReorderParticipants({
        participants: data,
        dragObject,
        endIndex,
        dropType,
        setEntryPositionsState,
      }),
    [data, setEntryPositionsState],
  );

  const options: TableOptions<ParticipantInterface> = useMemo(
    () => ({
      columns,
      data,
      enableMultiRowSelection: isEnableMultiRowSelection || false,
      getRowId,
      state: {
        columnVisibility,
        rowSelection,
        sorting,
      },
      onColumnVisibilityChange: setColumnVisibility,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
    }),
    [columns, data, isEnableMultiRowSelection, columnVisibility, rowSelection, sorting],
  );

  const tableInstance = useTableProp<ParticipantInterface>(options);
  const { setGlobalFilter, resetSorting } = tableInstance;
  const { columnVisibility: columnsVisibility, globalFilter } = tableInstance.getState();
  const selectedFlatRows = tableInstance.getSelectedRowModel().flatRows;
  const { rows } = tableInstance.getRowModel();

  useEffect(() => {
    if (isEditReorder) resetSorting(true);
  }, [tableInstance, isEditReorder, resetSorting]);

  const handleResetGlobalFilter = useCallback(() => {
    setGlobalFilter('');
    searchRef.current.value = '';
  }, [setGlobalFilter]);

  columnsRef.current = useMemo(() => Object.keys(columnsVisibility), [columnsVisibility]);

  return useMemo(
    () => (
      <>
        {!disableSearch && (
          <SearchInput
            inputRef={searchRef}
            placeholder="Search participants"
            sx={{ marginTop: 2 }}
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            onClear={handleResetGlobalFilter}
          />
        )}
        <EnhancedTable
          additionalButtons={additionalButtons}
          columnsVisibility={columnsVisibility}
          rowConfig={drawRowConfig}
          rows={rows}
          saveColumnsSelection={saveColumnsSelection}
          selectedActions={selectedActions ? selectedActions(selectedFlatRows, resetRowSelection) : undefined}
          tableInstance={tableInstance}
          onDrop={handleOnDrop}
        />
      </>
    ),
    [
      disableSearch,
      globalFilter,
      handleResetGlobalFilter,
      additionalButtons,
      columnsVisibility,
      drawRowConfig,
      rows,
      saveColumnsSelection,
      selectedActions,
      selectedFlatRows,
      tableInstance,
      handleOnDrop,
      setGlobalFilter,
      resetRowSelection,
    ],
  );
};

export default DrawParticipantsTable;
